/**
 * DataSyncSettings Component
 *
 * Main container for data synchronization settings.
 * Orchestrates all sync-related UI: provider selection, storage location,
 * sync configuration, status display, and device management.
 */

// Session storage key for migration tracking (cleared when tab closes)
const MIGRATION_SESSION_KEY = 'sync-settings-migration-complete';

// 1. External dependencies
import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';

// 2. Internal absolute imports
import { useAuth } from '@/hooks/useAuth';
import { useSyncConnection } from '@/contexts';
import { useToastNotification } from '@enfomentis/ui';
import { BackgroundSyncService } from '@/services/sync/BackgroundSyncService';
import { SyncSettingsService } from '@/services/SyncSettingsService';
import { userSyncSettingsService } from '@/services/UserSyncSettingsService';
import { migrateLocalSyncSettingsToServer } from '@/services/migrations/migrateLocalSyncSettings';
import { DeviceService } from '@/services/DeviceService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { createProvider } from '@/services/sync/providerFactory';
import { LocalDirectorySyncProvider } from '@/services/sync/LocalDirectorySyncProvider';
// NOTE: Old directory-level conflict detection temporarily disabled while implementing section-level conflicts
// import { detectSyncConflict } from '@/services/sync/syncConflictDetection';
import { getOrCreateDeviceId } from '@/utils/deviceDetection';
import type { SyncSettings } from '@/types/sync/SyncSettings';
import type { UserSyncSettings } from '@/types/supabase/UserSyncSettings';
import type { UserSyncDevice } from '@/types/supabase/UserSyncDevice';
import type { SyncStatus } from '@/services/sync/BackgroundSyncService';
import type { SyncResult } from '@/types/sync/SyncExport';
import type { SyncProviderType, ProviderKey, SchemaVersionError } from '@/types/sync';
// import type { ConflictInfo } from '@/services/sync/syncConflictDetection';

// 3. Relative imports - child components
import { BrowserCompatibilityAlert } from './BrowserCompatibilityAlert';
import { SyncProviderSelector } from './SyncProviderSelector';
import { StorageLocationCard } from './StorageLocationCard';
import { GoogleDriveProviderCard } from './GoogleDriveProviderCard';
import { SyncConfigurationCard } from './SyncConfigurationCard';
import { SyncStatusCard } from './SyncStatusCard';
import { DeviceManagementCard } from './DeviceManagementCard';
import { ProviderSwitchModal } from './ProviderSwitchModal';
import type { ProviderSwitchChoiceData, PreviousConnectionInfo } from './ProviderSwitchModal';
import { ResetSyncConfirmationModal } from './ResetSyncConfirmationModal';
// ReconnectionBanner now rendered globally in PrivateAppProvider
import { Skeleton } from '@enfomentis/ui';

/**
 * Data Sync Settings Component
 *
 * Main orchestrator for sync settings UI.
 * Manages shared state and passes data/handlers to child components.
 */
export const DataSyncSettings: React.FC = () => {
  // Services
  const { user, authState } = useAuth();
  const { showToast } = useToastNotification();
  const [syncService] = useState(() => BackgroundSyncService.getInstance());
  const [localSettingsService] = useState(() => new SyncSettingsService()); // For directoryId only
  // userSyncSettingsService singleton imported at top - use that instead of creating instance
  const [deviceService] = useState(() => new DeviceService());

  // Provider connection hook for managing sync provider connections
  const {
    state: connectionState,
    connect: connectProvider,
    disconnect: disconnectProvider,
    abort: abortConnection
  } = useSyncConnection();

  // Shared data state - HYBRID: local device state + universal user preferences
  const [localSettings, setLocalSettings] = useState<SyncSettings | null>(null); // directoryId only
  const [userSettings, setUserSettings] = useState<UserSyncSettings | null>(null); // universal prefs
  const [devices, setDevices] = useState<UserSyncDevice[]>([]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  // Cloud sync state (Google Drive)
  const [isCloudConnected, setIsCloudConnected] = useState<boolean>(false);
  const [cloudConnectionInfo, setCloudConnectionInfo] = useState<{
    email: string | null;
    userId: string | null;
    folderId: string | null;
  } | null>(null);
  const [isCheckingCloudConnection, setIsCheckingCloudConnection] = useState<boolean>(false);

  // Refs to track latest state values for polling comparison (avoids stale closure in Strict Mode)
  const isCloudConnectedRef = useRef(isCloudConnected);
  const cloudConnectionInfoRef = useRef(cloudConnectionInfo);

  // Keep refs in sync with state (avoids stale closure issue in polling interval)
  useEffect(() => {
    isCloudConnectedRef.current = isCloudConnected;
    cloudConnectionInfoRef.current = cloudConnectionInfo;
  }, [isCloudConnected, cloudConnectionInfo]);

  // Provider switch modal state
  const [providerSwitchModal, setProviderSwitchModal] = useState<{
    isOpen: boolean;
    variant: 'reconnect' | 'connect';
    providerKey: ProviderKey | null;
    previousConnection: PreviousConnectionInfo | null;
  }>({
    isOpen: false,
    variant: 'connect',
    providerKey: null,
    previousConnection: null,
  });

  // Provider switching state
  const [isSwitching, setIsSwitching] = useState(false);

  // Schema version error state
  const [schemaVersionError, setSchemaVersionError] = useState<SchemaVersionError | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Ref for provider switch modal resolver (avoids window global pollution)
  const modalResolveRef = useRef<((choiceData: ProviderSwitchChoiceData) => void) | null>(null);

  // Store origin tab ID once at mount for cross-tab comparison
  const tabOriginIdRef = useRef<string | null>(null);

  // Toast deduplication: Track recent toasts by correlationId + providerKey
  // Prevents toast storms from cross-tab events
  const toastCacheRef = useRef<Map<string, number>>(new Map());
  const TOAST_DEDUPE_WINDOW_MS = 30_000; // 30 seconds (keeps memory bounded for typical usage)

  /**
   * Check if a toast should be shown based on deduplication policy.
   *
   * @param kind - Type of toast event (cross-tab success or failure)
   * @param providerKey - Provider identifier for cache key
   * @param correlationId - Optional correlation ID for deduplication
   * @param bypassVisibilityCheck - If true, show toast even when tab is hidden (for origin tab user actions)
   * @returns true if toast should be shown (not recently shown and passes visibility check)
   */
  const shouldShowToast = useCallback((
    kind: 'cross-tab-success' | 'cross-tab-failure',
    providerKey: ProviderKey,
    correlationId?: string,
    bypassVisibilityCheck = false
  ): boolean => {
    // SSR safety: skip if document is unavailable
    if (typeof document === 'undefined') {
      return false;
    }

    // Skip if tab is hidden (visibility policy), unless bypassed for origin tab user actions
    if (!bypassVisibilityCheck && document.visibilityState !== 'visible') {
      return false;
    }

    // Generate cache key
    const cacheKey = `${kind}:${providerKey}:${correlationId || 'no-correlation'}`;

    // Check if recently shown
    const lastShown = toastCacheRef.current.get(cacheKey);
    const now = Date.now();

    if (lastShown && (now - lastShown) < TOAST_DEDUPE_WINDOW_MS) {
      return false; // Recently shown, skip
    }

    // Update cache
    toastCacheRef.current.set(cacheKey, now);

    // Cleanup old entries (keep cache size bounded, soft cap at 100 entries)
    // Typical usage: ~few providers × ~few event types × 30s rolling window = <20 entries
    if (toastCacheRef.current.size > 100) {
      const oldestAllowed = now - TOAST_DEDUPE_WINDOW_MS;
      for (const [key, timestamp] of toastCacheRef.current.entries()) {
        if (timestamp < oldestAllowed) {
          toastCacheRef.current.delete(key);
        }
      }
    }

    return true; // Show toast
  }, []);

  // Effect: Initialize origin tab ID for cross-tab coordination
  useEffect(() => {
    // Get origin ID from syncCoordinatorService (stable across component lifecycle)
    tabOriginIdRef.current = syncCoordinatorService.getOriginId();
  }, []);

  // Cleanup: abort connection on unmount to prevent orphaned pickers/popups
  useEffect(() => {
    return () => {
      abortConnection();
    };
  }, [abortConnection]);

  // Cleanup: clear toast cache on unmount
  useEffect(() => {
    const cache = toastCacheRef.current;
    return () => {
      cache.clear();
    };
  }, []);

  // Rising-edge detection: Update Google Drive card state when connection succeeds
  // Fixes Phase 6A bug where synchronous state check missed async hook state update
  const prevStatusRef = useRef(connectionState.status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = connectionState.status;

    console.log(`[DataSyncSettings] Rising-edge effect: prev=${prev}, current=${connectionState.status}`);

    // Type guard: only success state has providerKey
    if (connectionState.status !== 'success') return;

    const justSucceeded =
      prev !== 'success' &&
      connectionState.providerKey === 'google-drive';

    console.log(`[DataSyncSettings] Rising-edge: justSucceeded=${justSucceeded}, providerKey=${connectionState.providerKey}`);

    if (!justSucceeded) return;

    let cancelled = false;

    console.log('[DataSyncSettings] Rising-edge: Fetching connection info...');
    (async () => {
      try {
        const info = await userSyncSettingsService.checkGoogleDriveConnection();
        console.log('[DataSyncSettings] Rising-edge: Got connection info:', info);
        if (cancelled) return;
        if (info) {
          setCloudConnectionInfo({
            email: info.email,
            userId: info.userId,
            folderId: info.folderId
          });
          setIsCloudConnected(!!info.connected);
          console.log(`[DataSyncSettings] Rising-edge: Set isCloudConnected to ${!!info.connected}`);
        }
      } catch (error) {
        console.error('Failed to fetch connection info after hook success:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [connectionState]);

  // Merged settings for component use (Option A.1 pattern)
  // Combines server-side universal preferences + device-specific directoryId + cache metadata
  const settings = useMemo(() => {
    if (!userSettings) return null;

    const merged = {
      ...userSettings,
      directoryId: localSettings?.directoryId || null,
      lastServerSync: localSettings?.lastServerSync || null
    };

    // DEV MODE: Override activeSyncProvider for E2E testing
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const mockProvider = params.get('mockProvider');
      if (mockProvider === 'local' || mockProvider === 'google-drive') {
        merged.activeSyncProvider = mockProvider;
      }
    }

    return merged;
  }, [userSettings, localSettings]);

  // Load local settings (directoryId only - device-specific, local provider only)
  const loadLocalSettings = useCallback(async (): Promise<void> => {
    try {
      const loaded = await localSettingsService.getOrCreateDefault();
      setLocalSettings(loaded);
    } catch (error) {
      console.error('Failed to load local settings:', error);
    }
  }, [localSettingsService]);

  // Load user settings (universal preferences - server-side)
  const loadUserSettings = useCallback(async (): Promise<UserSyncSettings | null> => {
    if (!user?.id) return null;

    try {
      const loaded = await userSyncSettingsService.getOrCreateSettings(user.id);
      setUserSettings(loaded);
      return loaded;
    } catch (error) {
      console.error('Failed to load user settings:', error);
      showToast({
        title: 'Error Loading Settings',
        description: 'Failed to load sync settings. Please refresh the page.',
        color: 'danger'
      });
      return null;
    }
  }, [user?.id, showToast]);

  // Load devices from Supabase
  const loadDevices = useCallback(async (): Promise<void> => {
    if (!user?.id) return;

    try {
      const userDevices = await deviceService.getUserDevices(user.id);
      setDevices(userDevices);
    } catch (error) {
      // Silently fail - devices table might not exist yet or user has no devices
      console.log('No devices found or error loading devices:', error);
      setDevices([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Check provider connection status
  const checkConnection = useCallback(async (): Promise<void> => {
    try {
      const provider = createProvider('local');
      if (!provider) {
        throw new Error('Failed to create local provider');
      }
      const connected = await provider.isConnected();
      setIsConnected(connected);
    } catch (error) {
      console.log('Failed to check connection:', error);
      setIsConnected(false);
    }
  }, []);

  // Load last sync info from manifest (if exists)
  const loadLastSync = useCallback(async (): Promise<void> => {
    try {
      const lastSync = await syncService.loadLastSyncFromManifest();
      if (lastSync) {
        setLastSyncResult(lastSync);
        console.log('📂 Loaded last sync info on initialization:', lastSync.dataExportedAt);
      }
    } catch (error) {
      console.log('Could not load last sync info:', error);
    }
  }, [syncService]);

  // Check Google Drive connection status
  // Can optionally take settings parameter to avoid race condition with React state updates
  const checkCloudConnection = useCallback(async (settingsOverride?: UserSyncSettings | null): Promise<void> => {
    const activeProvider = settingsOverride?.activeSyncProvider ?? userSettings?.activeSyncProvider;

    if (activeProvider === 'google-drive') {
      setIsCheckingCloudConnection(true);
      try {
        // Dynamic import check before using factory
        await import('@/services/sync/GoogleDriveSyncProvider');
        const provider = createProvider('google-drive');
        if (!provider) {
          throw new Error('Failed to create Google Drive provider');
        }
        // Always check connection info from service (even if disconnected)
        // Service returns email/folderId even with expired tokens for reconnect UX
        const connectionInfo = await userSyncSettingsService.checkGoogleDriveConnection();

        if (connectionInfo) {
          setCloudConnectionInfo({
            email: connectionInfo.email,
            userId: connectionInfo.connected ? connectionInfo.userId : null, // Clear userId if disconnected
            folderId: connectionInfo.folderId
          });
          setIsCloudConnected(!!connectionInfo.connected);
        } else {
          // No connection info at all (never connected)
          setIsCloudConnected(false);
          setCloudConnectionInfo({
            email: null,
            userId: null,
            folderId: null
          });
        }
      } catch (error) {
        console.log('Failed to check cloud connection:', error);
        setIsCloudConnected(false);
        // On error, also preserve non-sensitive display data
        setCloudConnectionInfo(prev => ({
          email: prev?.email ?? null,
          userId: null,
          folderId: prev?.folderId ?? null
        }));
      } finally {
        setIsCheckingCloudConnection(false);
      }
    }
  }, [userSettings?.activeSyncProvider]);

  /**
   * Check if a prior connection exists for the given provider.
   * Returns connection metadata if available, null otherwise.
   *
   * Source of truth hierarchy:
   * 1. Google Drive: Service call to checkGoogleDriveConnection() (reads from oauth_cloud_tokens + user_sync_settings)
   * 2. Local Directory: Read persisted directoryId from localSettings (IndexedDB)
   * 3. Fallback: Card state (only if service read fails)
   */
  const checkPriorConnection = useCallback(async (providerKey: ProviderKey): Promise<{
    exists: boolean;
    email?: string | null;
    directoryName?: string | null;
  }> => {
    if (providerKey === 'google-drive') {
      try {
        // Source of truth: Service read from database
        const connectionInfo = await userSyncSettingsService.checkGoogleDriveConnection();

        if (connectionInfo && connectionInfo.email) {
          return {
            exists: true,
            email: connectionInfo.email,
          };
        }

        // Fallback: Check card state (may be stale)
        if (cloudConnectionInfo?.email) {
          return {
            exists: true,
            email: cloudConnectionInfo.email,
          };
        }

        return { exists: false };
      } catch (error) {
        console.log('Failed to check Google Drive prior connection:', error);

        // Fallback: Check card state
        if (cloudConnectionInfo?.email) {
          return {
            exists: true,
            email: cloudConnectionInfo.email,
          };
        }

        return { exists: false };
      }
    }

    if (providerKey === 'local') {
      try {
        // Source of truth: IndexedDB localSettings (directoryId)
        const settings = localSettings || await localSettingsService.getOrCreateDefault();

        if (settings?.directoryId) {
          // DirectoryId exists - we have a prior connection
          // Note: We don't have directoryName in persisted state, so can't display it
          return {
            exists: true,
            directoryName: null, // Not persisted
          };
        }

        return { exists: false };
      } catch (error) {
        console.log('Failed to check Local Directory prior connection:', error);
        return { exists: false };
      }
    }

    // Unknown provider
    return { exists: false };
  }, [localSettings, localSettingsService, cloudConnectionInfo]);

  // Initial data load with migration check
  useEffect(() => {
    const initializeData = async (): Promise<void> => {
      setIsLoading(true);

      // Run migration once per session (sessionStorage cleared when tab closes)
      if (user?.id && !sessionStorage.getItem(MIGRATION_SESSION_KEY)) {
        try {
          await migrateLocalSyncSettingsToServer(user.id);
          // Mark migration complete for this session
          sessionStorage.setItem(MIGRATION_SESSION_KEY, 'true');
        } catch (error) {
          console.error('Failed to migrate sync settings:', error);
          // Don't block loading on migration failure
        }
      }

      // CRITICAL: Load user settings first (establishes activeSyncProvider)
      // checkCloudConnection() depends on userSettings.activeSyncProvider
      const loadedUserSettings = await loadUserSettings();

      // Now safe to fan out - pass loaded settings directly to avoid React state race
      await Promise.all([
        loadLocalSettings(), // Load directoryId from IndexedDB
        loadDevices(),
        checkConnection(),
        checkCloudConnection(loadedUserSettings), // Pass loaded settings directly
        loadLastSync()
      ]);
      setIsLoading(false);
    };

    // Only initialize when we have a user AND auth is no longer loading
    // Note: There may still be transient 401s on first load due to Supabase client
    // JWT timing - these are handled gracefully and don't affect functionality
    if (user?.id && !authState.isLoading) {
      initializeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, authState.isLoading]);

  // Subscribe to device updates (Task 2.9 - Option 2 architecture - Page-specific)
  // Device list is only relevant when viewing this page, so subscribe here (not globally)
  // Service manages channel lifecycle, component receives domain objects
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = deviceService.subscribeToDeviceChanges(
      user.id,
      (action) => {
        if (action.type === 'DELETE' && action.deletedId) {
          // Remove from list
          setDevices(prev => prev.filter(d => d.id !== action.deletedId));
        } else if (action.device) {
          // INSERT or UPDATE
          const device = action.device;
          setDevices(prev => {
            const index = prev.findIndex(d => d.id === device.id);
            if (index >= 0) {
              // Update existing device
              const next = [...prev];
              next[index] = device;
              // Sort by lastSyncAt DESC to maintain canonical ordering
              return next.sort((a, b) => {
                if (!a.lastSyncAt) return 1;
                if (!b.lastSyncAt) return -1;
                return new Date(b.lastSyncAt).getTime() - new Date(a.lastSyncAt).getTime();
              });
            } else {
              // Insert new device and sort
              const next = [...prev, device];
              return next.sort((a, b) => {
                if (!a.lastSyncAt) return 1;
                if (!b.lastSyncAt) return -1;
                return new Date(b.lastSyncAt).getTime() - new Date(a.lastSyncAt).getTime();
              });
            }
          });
        }

        // No toast - silent update per UX rules
      }
    );

    return unsubscribe;
  }, [user?.id, deviceService]);

  // Reload user settings when cache is updated by AppWrapper's realtime subscription (Task 2.10)
  // AppWrapper updates IndexedDB cache via applyRealtimeUpdate() when settings change in another tab
  // This component needs to refetch from service to pick up those cached changes
  // NEW (Task 2.8 Phase 1B): Also polls connection status cache for cross-tab UI sync
  useEffect(() => {
    if (!user?.id) return;

    // Poll every 2 seconds while Settings page is visible
    // This picks up cache updates from AppWrapper's realtime subscription
    const intervalId = setInterval(async () => {
      try {
        // Poll settings (existing behavior)
        const freshSettings = await userSyncSettingsService.getOrCreateSettings(user.id);
        // Only update if actually changed (prevents unnecessary re-renders)
        if (JSON.stringify(freshSettings) !== JSON.stringify(userSettings)) {
          setUserSettings(freshSettings);
        }

        // Poll connection status (NEW for Task 2.8 Phase 1B)
        // Enables cross-tab UI sync when connection events occur in other tabs
        const cachedConnection = userSyncSettingsService.getCachedConnectionStatus();

        if (cachedConnection) {
          // Deep comparison using refs (not closed-over state vars) to avoid stale closure
          const hasConnectionChanged =
            cachedConnection.isConnected !== isCloudConnectedRef.current ||
            cachedConnection.email !== cloudConnectionInfoRef.current?.email ||
            cachedConnection.folderId !== cloudConnectionInfoRef.current?.folderId;

          if (hasConnectionChanged) {
            setIsCloudConnected(cachedConnection.isConnected);
            setCloudConnectionInfo({
              email: cachedConnection.email,
              userId: cloudConnectionInfoRef.current?.userId || null,
              // Preserve existing folderId if cache doesn't have it (prevents flickering)
              folderId: cachedConnection.folderId || cloudConnectionInfoRef.current?.folderId || null
            });
          }
        }
      } catch (_error) {
        // Silent failure - don't spam console during normal operation
      }
    }, 2000);

    return () => clearInterval(intervalId);
  }, [user?.id, userSettings]); // Singleton is stable, no need in deps. Removed isCloudConnected, cloudConnectionInfo from deps

  // Effect: Cross-tab reconnection success toast (passive tabs only)
  useEffect(() => {
    // Subscribe to BroadcastChannel for cross-tab reconnection events
    const handleReconnectionMessage = (event: MessageEvent) => {
      const message = event.data;

      // Filter for reconnect:end success messages
      if (
        message.type === 'reconnect:end' &&
        message.success === true &&
        message.provider &&
        message.correlationId
      ) {
        // Check if this is a passive tab (not the origin)
        const isOriginTab = message.originId === tabOriginIdRef.current;

        if (!isOriginTab) {
          // Passive tab: show info toast per toast policy (Section 11, row 2)
          if (shouldShowToast('cross-tab-success', message.provider, message.correlationId)) {
            const providerName = message.provider === 'google-drive' ? 'Google Drive' : 'Local Directory';

            showToast({
              title: 'Reconnected',
              description: `${providerName} reconnected in another tab.`,
              color: 'default',  // Use 'default'/'info' tone (ambient, non-action)
              timeout: 4000,
            });
          }

          // Refresh card state silently (no modal)
          if (message.provider === 'google-drive') {
            checkCloudConnection().catch(err => {
              console.error('[Cross-tab] Failed to refresh cloud connection:', err);
            });
          }
        }
      }
    };

    // Get BroadcastChannel from syncCoordinatorService
    // Returns null if BroadcastChannel unavailable (SSR or unsupported browsers)
    const channel = syncCoordinatorService.getChannel();
    if (channel) {
      channel.addEventListener('message', handleReconnectionMessage);
    }

    return () => {
      if (channel) {
        channel.removeEventListener('message', handleReconnectionMessage);
      }
    };
  }, [shouldShowToast, showToast, checkCloudConnection]);

  // Subscribe to BackgroundSyncService status updates
  useEffect(() => {
    const unsubscribeStatus = syncService.onStatusChanged((status: SyncStatus) => {
      setSyncStatus(status);
    });

    const unsubscribeCompleted = syncService.onSyncCompleted((result: SyncResult) => {
      console.log('🔔 onSyncCompleted callback fired:', result.success);
      // Merge with existing lastSyncResult to preserve filename from previous syncs
      // when current sync had no changes (didn't write a new file)
      setLastSyncResult(prev => ({
        ...result,
        filename: result.filename || prev?.filename || '',
      }));

      // Reload devices after successful sync
      if (result.success && user?.id) {
        console.log('🔄 Reloading devices after sync...');
        deviceService.getUserDevices(user.id)
          .then(userDevices => {
            console.log('✅ Devices reloaded:', userDevices.length, 'devices');
            setDevices(userDevices);
          })
          .catch(error => {
            console.log('❌ Failed to reload devices after sync:', error);
          });
      }
    });

    // Cleanup: unsubscribe when component unmounts or syncService changes
    return () => {
      unsubscribeStatus();
      unsubscribeCompleted();
    };
  }, [syncService, user?.id, deviceService]);

  // Subscribe to schema version errors
  useEffect(() => {
    // Initialize from current value (hydrate on mount)
    const initialError = syncService.getSchemaVersionError();
    setSchemaVersionError(initialError);

    // Subscribe to changes
    const unsubscribe = syncService.onSchemaVersionError((error) => {
      setSchemaVersionError(error);
    });

    return () => unsubscribe();
  }, [syncService]);

  // Auto-clear modal state when error clears
  useEffect(() => {
    if (schemaVersionError === null) {
      setShowResetModal(false);
      setIsResetting(false);
    }
  }, [schemaVersionError]);

  /**
   * Provider Switch Orchestrator
   *
   * Handles switching between sync providers with intelligent reconnection logic.
   * Shows modal prompting user to reconnect to previous connection or start fresh.
   *
   * Flow:
   * 1. Check if sync is running → stop it (idempotent)
   * 2. Check for prior connection (service read, not stale state)
   * 3. Show ProviderSwitchModal (reconnect or new connection)
   * 4. Handle user choice:
   *    - reconnect: Use cached tokens/directory
   *    - new: Start fresh (account chooser for Drive, new directory for Local)
   *    - cancel: Revert dropdown to previous provider
   * 5. On success: Settings updated, sync starts
   * 6. On failure: Revert dropdown, show error toast
   */
  const handleProviderSwitch = useCallback(async (newProviderKey: ProviderKey): Promise<void> => {
    if (!userSettings || !user?.id) {
      console.warn('[handleProviderSwitch] No user settings or user ID');
      return;
    }

    const prevProviderKey = userSettings.activeSyncProvider as ProviderKey | 'none' | undefined;

    // Guard: Don't switch to same provider
    if (newProviderKey === prevProviderKey) {
      return;
    }

    // Guard: Disable dropdown during switch
    setIsSwitching(true);

    try {
      // Step 1: Stop sync if running (idempotent)
      try {
        await syncService.stop();
      } catch (error) {
        // Swallow "already stopped" errors
        console.log('[handleProviderSwitch] Stop sync result:', error);
      }

      // Step 2: Check for prior connection (source of truth: service read)
      const priorConnection = await checkPriorConnection(newProviderKey);

      // Step 3: Show modal and wait for user choice
      // Modal opens popup synchronously on button click and returns it with choice data
      const choiceData = await new Promise<ProviderSwitchChoiceData>((resolve) => {
        // Store resolver in ref (scoped, avoids window global pollution)
        modalResolveRef.current = resolve;

        setProviderSwitchModal({
          isOpen: true,
          variant: priorConnection.exists ? 'reconnect' : 'connect',
          providerKey: newProviderKey,
          previousConnection: priorConnection.exists
            ? {
                email: priorConnection.email ?? null,
                directoryName: priorConnection.directoryName ?? null,
              }
            : null,
        });
      });

      // Step 4: Handle user choice
      if (choiceData.choice === 'cancel') {
        // Revert dropdown to previous provider
        // This happens synchronously in the finally block via settings reload
        return;
      }

      if (choiceData.choice === 'reconnect') {
        // Reconnect to previous connection
        if (newProviderKey === 'google-drive') {
          // Pass pre-opened popup from modal (opened synchronously on button click)
          await handleGoogleDriveConnect(false, choiceData.oauthPopup);
        } else if (newProviderKey === 'local') {
          // Reconnect uses cached directory (forceNew = false)
          await handleConnect(false);
        }
      } else if (choiceData.choice === 'new') {
        // Start fresh connection
        if (newProviderKey === 'google-drive') {
          // Use account chooser (promptMode: 'select_account')
          // Pass pre-opened popup from modal (opened synchronously on button click)
          await handleGoogleDriveConnect(true, choiceData.oauthPopup);
        } else if (newProviderKey === 'local') {
          // Force new directory picker (forceNew = true)
          // Note: handleConnect() already wraps with beforeunload suppression
          await handleConnect(true);
        }
      }

      // Success - settings already updated by connect handlers
      console.log('✅ [handleProviderSwitch] Provider switched successfully');
    } catch (error) {
      console.error('[handleProviderSwitch] Failed to switch provider:', error);

      // Revert dropdown to previous provider
      await loadUserSettings();

      showToast({
        title: 'Provider Switch Failed',
        description: error instanceof Error ? error.message : 'Failed to switch provider',
        color: 'danger',
      });
    } finally {
      // Always clear switching flag, resolver ref, and reload settings
      setIsSwitching(false);
      modalResolveRef.current = null;
      setProviderSwitchModal({
        isOpen: false,
        variant: 'connect',
        providerKey: null,
        previousConnection: null,
      });
      await loadUserSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettings, user?.id, syncService, checkPriorConnection, loadUserSettings, showToast]);

  /**
   * Handle provider switch modal choice callback.
   * For cloud providers (google-drive), choiceData includes pre-opened OAuth popup
   * to avoid browser popup blocking.
   */
  const handleProviderSwitchChoice = useCallback((choiceData: ProviderSwitchChoiceData): void => {
    // Resolve the promise stored by handleProviderSwitch (ref-based, scoped)
    if (modalResolveRef.current) {
      modalResolveRef.current(choiceData);
      modalResolveRef.current = null;
    }
  }, []);

  // Handler: Change sync provider
  const handleProviderChange = async (provider: SyncProviderType): Promise<void> => {
    if (!userSettings || !user?.id) return;

    // Check if it's a valid ProviderKey ('google-drive' or 'local')
    if (provider === 'google-drive' || provider === 'local') {
      // Use orchestrator for actual providers
      await handleProviderSwitch(provider);
      return;
    }

    // Fallback: Direct settings update for other cases (e.g. future providers)
    // This includes the implicit 'none' case if settings are cleared
    try {
      // Stop sync service if switching away from a provider
      await syncService.stop();

      await userSyncSettingsService.updateActiveSyncProvider(user.id, provider);
      await loadUserSettings();

      showToast({
        title: 'Provider Updated',
        description: `Sync provider changed to ${provider}`,
        color: 'success'
      });
    } catch (error) {
      console.error('Failed to update provider:', error);
      showToast({
        title: 'Error',
        description: 'Failed to update sync provider',
        color: 'danger'
      });
    }
  };

  // Handler: Connect to local directory
  const handleConnect = async (forceNew = false): Promise<void> => {
    try {
      // Suppress beforeunload during directory picker
      syncService.suppressBeforeUnloadHandler();

      // Create provider via factory (not using hook due to directoryId access requirement)
      // CRITICAL: Must use SAME instance for connect(), getDirectoryId(), and BackgroundSyncService
      // The FileSystemDirectoryHandle lives on this instance after connect()
      const provider = createProvider('local');
      if (!provider) {
        throw new Error('Failed to create local directory provider');
      }
      // Type assertion needed to access Local Directory-specific getDirectoryId() method
      const localProvider = provider as LocalDirectorySyncProvider;

      const success = await localProvider.connect(forceNew ? { forceNewSelection: true } : undefined);

      // Only show success toast if user actually selected a directory
      if (success) {
        // Update connection state
        setIsConnected(true);

        // Get directory ID immediately (creates .app-directory-id if doesn't exist)
        // This ensures the ID file exists for both first connection and directory changes
        const newDirectoryId = await localProvider.getDirectoryId();
        console.log('🆔 Directory ID:', newDirectoryId);

        // If changing directory, check for conflicts
        if (forceNew) {
          console.log('🔄 forceNew=true, checking for conflicts...');
          console.log('🆔 New directory ID:', newDirectoryId);
          console.log('🆔 Current directory ID:', settings?.directoryId);
          setLastSyncResult(null);

          // Load manifest from new directory
          const manifest = await syncService.loadManifestFromProvider(localProvider);
          console.log('📄 Manifest loaded:', !!manifest);

          // NOTE: Directory-level conflict detection temporarily disabled
          // Section-level conflict detection (Task 1.C) is now active
          // Directory conflicts will be re-enabled in future task
          // const deviceLastSync = syncService.getLastSyncTime();
          // console.log('⏰ Device last sync:', deviceLastSync);
          // console.log('🔍 Calling detectSyncConflict...');
          // const conflict = await detectSyncConflict(
          //   manifest,
          //   deviceLastSync,
          //   provider,
          //   settings?.directoryId || null
          // );
          // console.log('✅ detectSyncConflict returned:', conflict);

          // if (conflict.hasConflict) {
          //   // Show conflict modal - wait for user resolution
          //   setConflictInfo(conflict);
          //   setPendingProvider(provider);
          //   setShowConflictModal(true);
          //   return; // Stop here, modal will handle resolution
          // }

          // No conflict - update stored directory ID
          if (settings && newDirectoryId) {
            await handleSettingsChange({
              directoryId: newDirectoryId
            });
          }
        }

        // Initialize BackgroundSyncService and set provider
        if (!user?.id) {
          throw new Error('User not logged in');
        }

        const deviceId = getOrCreateDeviceId();
        await syncService.initialize(user.id, deviceId);

        // Set provider on BackgroundSyncService
        // Use the SAME instance that performed connect() - it holds the FileSystemDirectoryHandle
        syncService.setProvider(localProvider);

        // Capture previous settings for rollback (in case start() fails)
        const previousProvider = settings?.activeSyncProvider ?? null;
        const previousDirectoryId = settings?.directoryId ?? null;
        const previousSyncOnLogin = settings?.syncOnLogin ?? false;

        // Update settings AFTER setProvider() but BEFORE start()
        // Repository validation: checks provider is set (getProvider() !== null) ✅
        // Service validation: checks syncEnabled is true (happens in start()) ⏳
        if (settings) {
          await handleSettingsChange({
            syncEnabled: true,
            activeSyncProvider: 'local',
            directoryId: newDirectoryId,
            syncOnLogin: true
          });
        }

        // Now start the service
        // Service will check syncEnabled (now true) and start successfully
        try {
          await syncService.start();
        } catch (startError) {
          // Rollback: if start() fails, restore previous settings to prevent false "previously configured" state
          console.error('Failed to start BackgroundSyncService, rolling back settings:', startError);
          if (settings) {
            await handleSettingsChange({
              syncEnabled: false,
              activeSyncProvider: previousProvider,
              directoryId: previousDirectoryId,
              syncOnLogin: previousSyncOnLogin
            });
          }
          throw startError; // Re-throw to trigger outer catch
        }

        // Try to load last sync info from manifest (if directory has existing sync files)
        const lastSync = await syncService.loadLastSyncFromManifest();
        if (lastSync) {
          setLastSyncResult(lastSync);
          console.log('📂 Loaded last sync info from directory:', lastSync.dataExportedAt);

          showToast({
            title: forceNew ? 'Directory Changed' : 'Directory Connected',
            description: 'Successfully connected to local directory',
            color: 'success'
          });
        } else {
          // No manifest found - this is a blank directory
          console.log('📂 New/blank directory - triggering initial sync');

          // Perform initial sync to create baseline
          const syncResult = await syncService.syncNow();

          if (syncResult.success) {
            setLastSyncResult(syncResult);

            // Refresh device list to show updated last sync time
            await loadDevices();

            showToast({
              title: 'Initial Sync Complete',
              description: `Created baseline with ${syncResult.recordCount || 0} records across ${syncResult.tableCount || 0} tables`,
              color: 'success'
            });
          } else {
            showToast({
              title: 'Directory Connected',
              description: 'Connected to directory, but initial sync failed. You can retry with "Sync Now".',
              color: 'warning'
            });
          }
        }
      }

      // Re-enable beforeunload after all sync operations complete
      syncService.enableBeforeUnloadHandler();

      // If !success, user cancelled the picker - no need to show any message
    } catch (error) {
      // Re-enable beforeunload on error
      syncService.enableBeforeUnloadHandler();

      console.error('Failed to connect directory:', error);
      showToast({
        title: 'Connection Failed',
        description: error instanceof Error ? error.message : 'Failed to connect to directory',
        color: 'danger'
      });
      throw error; // Re-throw so StorageLocationCard can handle UI state
    }
  };

  // Handler: Disconnect from local directory
  const handleDisconnect = async (): Promise<void> => {
    try {
      // Create provider via factory
      const provider = createProvider('local');
      if (!provider) {
        throw new Error('Failed to create local provider');
      }
      await provider.disconnect();

      // Stop BackgroundSyncService
      syncService.stop();

      // Update connection state
      setIsConnected(false);

      // Clear last sync result
      setLastSyncResult(null);

      // Clear sync settings (disable sync, clear directory ID but preserve provider for "previously configured" state)
      if (settings) {
        await handleSettingsChange({
          syncEnabled: false,
          directoryId: null
        });
      }

      showToast({
        title: 'Directory Disconnected',
        description: 'Disconnected from local directory. Sync has been disabled.',
        color: 'success'
      });
    } catch (error) {
      console.error('Failed to disconnect directory:', error);
      showToast({
        title: 'Disconnect Failed',
        description: 'Failed to disconnect from directory',
        color: 'danger'
      });
    }
  };

  // handleBannerSuccess removed - banner now rendered globally in PrivateAppProvider

  // Handler: Connect to Google Drive
  // oauthPopup: Pre-opened popup window passed from modal (opened synchronously on button click)
  const handleGoogleDriveConnect = async (
    useAccountChooser = false,
    oauthPopup?: Window | null
  ): Promise<void> => {
    // Use hook to connect
    // Hook handles ALL orchestration: settings, BackgroundSyncService init/start, sync
    // Card state update is driven by rising-edge detection effect
    // Pass oauthPopup (pre-opened in modal) to avoid browser popup blocking
    const connectOptions: import('@/types/sync/SyncProvider').ProviderConnectOptions = {
      ...(useAccountChooser ? { promptMode: 'select_account' as const } : {}),
      ...(oauthPopup ? { oauthPopup } : {}),
    };
    console.log('[DataSyncSettings] Calling connectProvider...');
    const success = await connectProvider('google-drive', connectOptions);
    console.log(`[DataSyncSettings] connectProvider returned: ${success}`);

    // Gate success logic on hook result (prevents race condition)
    if (!success) {
      // Hook handled error state and UI signals - nothing more to do here
      console.log('[DataSyncSettings] connectProvider returned false, returning early');
      return;
    }

    // Connection succeeded - do component-specific post-connect tasks
    console.log('[DataSyncSettings] Running post-connect tasks...');
    // (Hook already did: settings update, BackgroundSyncService init/start, syncNow)
    try {
      // Enable syncOnLogin preference (user convenience - sync on every login)
      if (settings) {
        await handleSettingsChange({ syncOnLogin: true });
      }

      // Update card state to show connection success
      await checkCloudConnection();

      // Reload devices list (reflects any sync that hook performed)
      await loadDevices();

      // Update card state from database (ensures UI matches DB)
      console.log('[DataSyncSettings] Fetching connection info from BFF...');
      const connectionInfo = await userSyncSettingsService.checkGoogleDriveConnection();
      console.log('[DataSyncSettings] Connection info:', connectionInfo);
      if (connectionInfo) {
        setCloudConnectionInfo({
          email: connectionInfo.email,
          userId: connectionInfo.userId,
          folderId: connectionInfo.folderId
        });
        setIsCloudConnected(connectionInfo.connected);
        console.log(`[DataSyncSettings] Set isCloudConnected to: ${connectionInfo.connected}`);
      }
    } catch (postConnectError) {
      // Post-connect UI updates failed, but connection itself succeeded
      // Show warning toast so user knows to refresh if UI looks stale
      console.error('[DataSyncSettings] Post-connect UI update failed:', postConnectError);
      showToast({
        title: 'Connected with warnings',
        description: 'Google Drive connected, but some UI updates failed. Try refreshing the page.',
        color: 'warning'
      });
    }
  };

  // Handler: Disconnect from Google Drive
  const handleGoogleDriveDisconnect = async (): Promise<void> => {
    try {
      // Use hook to disconnect
      await disconnectProvider();

      // Invalidate connection cache to prevent stale data on reconnect
      // The provider's isConnected() method has a 30s cache TTL
      // Force a fresh check on next connection attempt
      await checkCloudConnection();

      // Stop BackgroundSyncService
      syncService.stop();

      // Update cloud connection state (preserve info for "reconnect" UI)
      setIsCloudConnected(false);
      // Preserve email/folderId for "reconnect" state, clear sensitive userId
      setCloudConnectionInfo(prev => ({
        email: prev?.email ?? null,
        userId: null, // Clear sensitive data
        folderId: prev?.folderId ?? null
      }));
      setLastSyncResult(null);

      // Clear sync settings (preserve activeSyncProvider for "previously configured" state)
      if (settings) {
        await handleSettingsChange({
          syncEnabled: false
        });
      }

      showToast({
        title: 'Google Drive Disconnected',
        description: 'Disconnected from Google Drive. Sync has been disabled.',
        color: 'success'
      });
    } catch (error) {
      console.error('Failed to disconnect Google Drive:', error);
      showToast({
        title: 'Disconnect Failed',
        description: 'Failed to disconnect from Google Drive',
        color: 'danger'
      });
    }
  };

  // Handler: Update sync settings (hybrid - routes to correct service)
  const handleSettingsChange = async (updates: Partial<UserSyncSettings & SyncSettings>): Promise<void> => {
    if (!user?.id) return;

    try {
      // Check if updating directoryId (device-specific, local provider only)
      if ('directoryId' in updates && localSettings) {
        const updatedLocal = await localSettingsService.update(localSettings.id, updates as Partial<SyncSettings>);
        setLocalSettings(updatedLocal);
      }

      // Extract universal preference updates (everything except directoryId)
      const { directoryId: _directoryId, ...universalUpdates } = updates;

      if (Object.keys(universalUpdates).length > 0 && userSettings) {
        const updatedUser = await userSyncSettingsService.updateSettingsWithRetry(user.id, universalUpdates);
        setUserSettings(updatedUser);

        // Update BackgroundSyncService with merged settings
        // Merge local (directoryId) + user (universal prefs) for BackgroundSyncService
        const mergedForSync: SyncSettings = {
          ...updatedUser,
          directoryId: localSettings?.directoryId || null,
          lastServerSync: localSettings?.lastServerSync || null
        };
        await syncService.updateSettings(mergedForSync);
      }

      // No success toast - UI changes are immediate and obvious
      // Only show errors if something goes wrong
    } catch (error) {
      console.error('Failed to update settings:', error);
      showToast({
        title: 'Error',
        description: 'Failed to update sync settings',
        color: 'danger'
      });
    }
  };

  // Handler: Manual sync trigger
  const handleSyncNow = async (): Promise<void> => {
    try {
      const result = await syncService.syncNow();

      if (result.success) {
        // Use appropriate terminology: baselines sync "records", deltas sync "operations"
        const countLabel = result.type === 'baseline' ? 'records' : 'operations';
        showToast({
          title: 'Sync Complete',
          description: `Synced ${result.recordCount || 0} ${countLabel} across ${result.tableCount || 0} tables`,
          color: 'success'
        });
      } else {
        showToast({
          title: 'Sync Failed',
          description: result.error || 'Unknown error occurred',
          color: 'danger'
        });
      }
    } catch (error) {
      console.error('Manual sync failed:', error);
      showToast({
        title: 'Sync Failed',
        description: error instanceof Error ? error.message : 'Failed to sync',
        color: 'danger'
      });
    }
  };

  // Handler: Reset sync click
  const handleResetClick = (): void => {
    setShowResetModal(true);
  };

  // Handler: Reset sync cancel
  const handleResetCancel = (): void => {
    setShowResetModal(false);
  };

  // Handler: Reset sync confirm
  const handleResetConfirm = async (): Promise<void> => {
    setIsResetting(true);
    try {
      const result = await syncService.rebaselineFromLocal();

      // Check result.success explicitly (don't rely on throw)
      if (result.success) {
        showToast({
          title: 'Success',
          description: 'Cloud sync data reset successfully',
          color: 'success'
        });
        setShowResetModal(false);
        // Error auto-clears via subscription
      } else {
        showToast({
          title: 'Reset Failed',
          description: result.error || 'Reset failed',
          color: 'danger'
        });
        // Keep modal open for retry
      }
    } catch (error) {
      console.error('Reset sync failed:', error);
      showToast({
        title: 'Reset Failed',
        description: error instanceof Error ? error.message : 'Unexpected error occurred',
        color: 'danger'
      });
      // Keep modal open for retry
    } finally {
      setIsResetting(false);
    }
  };

  // Handler: Remove device
  const handleRemoveDevice = async (deviceId: string): Promise<void> => {
    try {
      await deviceService.delete(deviceId);
      await loadDevices();

      showToast({
        title: 'Device Removed',
        description: 'Device unregistered successfully',
        color: 'success'
      });
    } catch (error) {
      console.error('Failed to remove device:', error);
      showToast({
        title: 'Error',
        description: 'Failed to remove device',
        color: 'danger'
      });
    }
  };

  // Handler: Rename device
  const handleRenameDevice = async (deviceId: string, newName: string): Promise<void> => {
    if (!user?.id) return;

    try {
      // Find the device to get its deviceId (UUID)
      const device = devices.find(d => d.id === deviceId);
      if (!device) {
        throw new Error('Device not found');
      }

      await deviceService.updateDeviceName(user.id, device.deviceId, newName);
      await loadDevices();

      showToast({
        title: 'Device Renamed',
        description: `Device renamed to "${newName}"`,
        color: 'success'
      });
    } catch (error) {
      console.error('Failed to rename device:', error);
      showToast({
        title: 'Error',
        description: 'Failed to rename device',
        color: 'danger'
      });
    }
  };

  // Get current device ID from localStorage (stable across sessions)
  const currentDeviceId = getOrCreateDeviceId();

  // Find current device from devices array (for database-sourced lastSyncAt)
  const currentDevice = useMemo(
    () => devices.find(d => d.deviceId === currentDeviceId),
    [devices, currentDeviceId]
  );

  /**
   * Banner visibility logic removed - banner now rendered globally in PrivateAppProvider
   * Banner = transient errors (OFFLINE / RATE_LIMITED / PROVIDER_OUTAGE / TIMEOUT).
   * Modal handles token/permission/directory errors.
   */

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-text-secondary">Loading sync settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Browser Compatibility Warning */}
      <BrowserCompatibilityAlert activeSyncProvider={settings?.activeSyncProvider ?? undefined} />

      {/* Sync Status & Actions - MOVED TO VERY TOP for immediate error visibility */}
      <SyncStatusCard
        status={syncStatus}
        lastSyncResult={lastSyncResult}
        lastSyncAt={currentDevice?.lastSyncAt ?? null}
        lastExportedAt={currentDevice?.lastExportedAt ?? null}
        lastImportedAt={currentDevice?.lastImportedAt ?? null}
        onSyncNow={handleSyncNow}
        isConnected={
          settings?.activeSyncProvider === 'local'
            ? isConnected
            : isCloudConnected
        }
        schemaVersionError={schemaVersionError}
        onResetClick={handleResetClick}
        isResetting={isResetting}
        connectedProvider={
          // Active provider from settings (service uses this value)
          (settings?.activeSyncProvider as ProviderKey | 'none' | undefined) ?? 'none'
        }
      />

      {/* Sync Provider Selection */}
      <SyncProviderSelector
        activeSyncProvider={settings?.activeSyncProvider ?? undefined}
        onChange={handleProviderChange}
        disabled={isSwitching}
      />

      {/* ReconnectionBanner now rendered globally in PrivateAppProvider */}
      {/* Removed local rendering to ensure banner persists across all pages */}

      {/* GOOGLE DRIVE CARD - show if google-drive provider selected */}
      {settings?.activeSyncProvider === 'google-drive' && (
        isCheckingCloudConnection ? (
          <Skeleton className="h-32 w-full rounded-lg" />
        ) : (
          <GoogleDriveProviderCard
            onConnect={(popup) => handleGoogleDriveConnect(false, popup)}
            onDisconnect={handleGoogleDriveDisconnect}
            isConnected={isCloudConnected}
            connectionInfo={cloudConnectionInfo}
            isConnectDisabled={
              connectionState.status === 'connecting' ||
              connectionState.status === 'rateLimited'
            }
          />
        )
      )}

      {/* LOCAL DIRECTORY CARD - show only if local provider selected */}
      {settings?.activeSyncProvider === 'local' && (
        <StorageLocationCard
          provider={settings?.activeSyncProvider ?? undefined}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
        />
      )}

      {/* Sync Configuration - show when ANY provider connected */}
      <SyncConfigurationCard
        settings={settings}
        onChange={handleSettingsChange}
        isConnected={
          settings?.activeSyncProvider === 'local'
            ? isConnected
            : isCloudConnected
        }
      />

      {/* Device Management */}
      <DeviceManagementCard
        devices={devices}
        currentDeviceId={currentDeviceId}
        onRemove={handleRemoveDevice}
        onRename={handleRenameDevice}
        lastSyncResult={lastSyncResult}
      />

      {/* Provider Switch Modal */}
      {providerSwitchModal.providerKey && (
        <ProviderSwitchModal
          isOpen={providerSwitchModal.isOpen}
          onOpenChange={(open) => {
            if (!open) {
              // Modal closed - treat as cancel
              handleProviderSwitchChoice({ choice: 'cancel' });
            }
            setProviderSwitchModal(prev => ({ ...prev, isOpen: open }));
          }}
          variant={providerSwitchModal.variant}
          providerKey={providerSwitchModal.providerKey}
          previousConnection={providerSwitchModal.previousConnection || undefined}
          onChoice={handleProviderSwitchChoice}
        />
      )}

      {/* Reset Sync Confirmation Modal */}
      <ResetSyncConfirmationModal
        isOpen={showResetModal}
        onClose={handleResetCancel}
        onConfirm={handleResetConfirm}
        isProcessing={isResetting}
        {...(settings?.activeSyncProvider &&
          (settings.activeSyncProvider === 'google-drive' || settings.activeSyncProvider === 'local')
            ? { providerKey: settings.activeSyncProvider }
            : {}
        )}
      />
    </div>
  );
};
